/**
 * VegaWidget — Agent proactif VEGA
 * Bouton header avec badge compteur + panneau slide-in (Insights | Chat).
 * Couleur emerald.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Radar, X, ChevronRight, AlertTriangle, AlertCircle, Info,
  Check, RefreshCw, Eye, EyeOff, Loader, Send, MessageSquare,
  Bell,
} from 'lucide-react';
import {
  getVegaInsights, getVegaInsightsSummary, dismissVegaInsight,
  dismissAllVegaInsights, runVegaAnalysis, vegaChat,
} from '../api';
import LoadingSpinner from './LoadingSpinner';

// ─── Severity config ─────────────────────────────────────────────────────────

const SEVERITY = {
  critical: { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50 border-red-200', label: 'Critique' },
  warning: { icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-50 border-orange-200', label: 'Attention' },
  info: { icon: Info, color: 'text-blue-500', bg: 'bg-blue-50 border-blue-200', label: 'Info' },
};

// ─── Insight Card ────────────────────────────────────────────────────────────

const InsightCard = ({ insight, onDismiss, onNavigate }) => {
  const sev = SEVERITY[insight.severity] || SEVERITY.info;
  const Icon = sev.icon;

  return (
    <div className={`${sev.bg} border rounded-lg p-3 transition-all hover:shadow-sm`}>
      <div className="flex items-start gap-2.5">
        <Icon size={18} className={`${sev.color} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 leading-tight">{insight.title}</h4>
          <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{insight.body}</p>
          <div className="flex items-center gap-2 mt-2">
            {insight.action_url && (
              <button
                onClick={() => onNavigate(insight.action_url.replace('/', ''))}
                className="text-xs text-emerald-700 font-medium hover:text-emerald-900 flex items-center gap-0.5"
              >
                {insight.action_label || 'Voir'} <ChevronRight size={12} />
              </button>
            )}
            <button
              onClick={() => onDismiss(insight.id)}
              className="text-xs text-gray-400 hover:text-gray-600 ml-auto flex items-center gap-0.5"
            >
              <EyeOff size={12} /> Masquer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Chat Message ────────────────────────────────────────────────────────────

const ChatMessage = ({ msg }) => {
  const isUser = msg.role === 'user';
  if (isUser) {
    return (
      <div className="flex justify-end mb-3">
        <div className="max-w-[80%] bg-emerald-600 text-white rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm leading-relaxed">
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div className="mb-4">
      <div className="flex items-center gap-1.5 mb-1.5">
        <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <Radar size={11} className="text-emerald-600" />
        </div>
        <span className="text-xs font-semibold text-emerald-700">VEGA</span>
      </div>
      <div className="text-sm leading-relaxed text-gray-800 whitespace-pre-wrap">{msg.content}</div>
    </div>
  );
};

// ─── Vega Button (header) ────────────────────────────────────────────────────

export const VegaButton = ({ onClick, summary }) => {
  const total = summary?.total || 0;
  const hasCritical = (summary?.critical || 0) > 0;

  return (
    <button
      onClick={onClick}
      className="relative p-2 hover:bg-primary-dark rounded transition"
      title={`VEGA — ${total} insight(s) actif(s)`}
    >
      <Radar size={18} className={hasCritical ? 'animate-pulse' : ''} />
      {total > 0 && (
        <span className={`absolute -top-0.5 -right-0.5 ${hasCritical ? 'bg-red-500' : 'bg-orange-400'} text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1`}>
          {total > 99 ? '99+' : total}
        </span>
      )}
    </button>
  );
};

// ─── Insights Tab ────────────────────────────────────────────────────────────

const InsightsTab = ({ isOpen, onNavigate, onClose, isAdmin }) => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState('all');

  const loadInsights = useCallback(async () => {
    setLoading(true);
    try {
      const params = { dismissed: 'false', limit: 100 };
      if (filter !== 'all') params.severity = filter;
      const res = await getVegaInsights(params);
      setInsights(res.data || []);
    } catch (err) {
      console.error('[VEGA] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (isOpen) loadInsights();
  }, [isOpen, loadInsights]);

  const handleDismiss = async (id) => {
    try {
      await dismissVegaInsight(id);
      setInsights(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      console.error('[VEGA] Dismiss error:', err);
    }
  };

  const handleDismissAll = async () => {
    try {
      await dismissAllVegaInsights({});
      setInsights([]);
    } catch (err) {
      console.error('[VEGA] Dismiss all error:', err);
    }
  };

  const handleRunAnalysis = async () => {
    setRunning(true);
    try {
      await runVegaAnalysis();
      await loadInsights();
    } catch (err) {
      console.error('[VEGA] Run error:', err);
    } finally {
      setRunning(false);
    }
  };

  const criticals = insights.filter(i => i.severity === 'critical');
  const warnings = insights.filter(i => i.severity === 'warning');
  const infos = insights.filter(i => i.severity === 'info');

  return (
    <>
      {/* Admin run button */}
      {isAdmin && (
        <div className="px-4 py-1.5 border-b border-gray-100 flex justify-end flex-shrink-0">
          <button
            onClick={handleRunAnalysis}
            disabled={running}
            className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1 font-medium"
          >
            <RefreshCw size={12} className={running ? 'animate-spin' : ''} />
            {running ? 'Analyse...' : 'Lancer une analyse'}
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-1.5 px-4 py-2 border-b border-gray-100 flex-shrink-0">
        {[
          { key: 'all', label: 'Tout', count: insights.length },
          { key: 'critical', label: 'Critiques', count: criticals.length },
          { key: 'warning', label: 'Attention', count: warnings.length },
          { key: 'info', label: 'Info', count: infos.length },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`text-xs px-2.5 py-1 rounded-full transition font-medium ${
              filter === f.key
                ? 'bg-emerald-100 text-emerald-800'
                : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            {f.label} {f.count > 0 && <span className="ml-0.5 text-[10px]">({f.count})</span>}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {loading ? (
          <div className="py-12">
            <LoadingSpinner size="default" />
          </div>
        ) : insights.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Check size={40} className="mb-3 text-emerald-400" />
            <p className="text-sm font-medium">Tout est en ordre</p>
            <p className="text-xs mt-1">Aucune alerte active pour le moment.</p>
          </div>
        ) : (
          insights.map(insight => (
            <InsightCard
              key={insight.id}
              insight={insight}
              onDismiss={handleDismiss}
              onNavigate={(view) => { onNavigate(view); onClose(); }}
            />
          ))
        )}
      </div>

      {/* Footer */}
      {insights.length > 0 && (
        <div className="border-t border-gray-100 px-4 py-2 flex-shrink-0 flex items-center justify-between">
          <span className="text-xs text-gray-400">{insights.length} insight(s)</span>
          <button
            onClick={handleDismissAll}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <Eye size={12} /> Tout masquer
          </button>
        </div>
      )}
    </>
  );
};

// ─── Chat Tab ────────────────────────────────────────────────────────────────

const ChatTab = ({ isOpen }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const msg = input.trim();
    if (!msg || sending) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setSending(true);

    try {
      const res = await vegaChat({ message: msg, conversationId });
      const data = res.data;
      setConversationId(data.conversationId);
      setMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erreur de communication avec VEGA.';
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setInput('');
  };

  return (
    <>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Radar size={36} className="mb-3 text-emerald-300" />
            <p className="text-sm font-medium text-gray-500">Posez une question a VEGA</p>
            <p className="text-xs mt-1 text-center max-w-[250px]">
              Etat des stocks, alertes, tendances, recommandations...
            </p>
            <div className="flex flex-wrap gap-1.5 mt-4 justify-center">
              {['Comment va ma creche ?', 'Des risques en cours ?', 'Resume de la semaine'].map(q => (
                <button
                  key={q}
                  onClick={() => { setInput(q); inputRef.current?.focus(); }}
                  className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full hover:bg-emerald-100 transition"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => (
              <ChatMessage key={i} msg={msg} />
            ))}
            {sending && (
              <div className="flex items-center gap-2 mb-3">
                <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Loader size={11} className="animate-spin text-emerald-600" />
                </div>
                <span className="text-xs text-gray-400">VEGA analyse...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-gray-100 p-3 flex-shrink-0">
        {conversationId && (
          <button
            onClick={handleNewChat}
            className="text-xs text-gray-400 hover:text-gray-600 mb-2 flex items-center gap-1"
          >
            <MessageSquare size={10} /> Nouvelle conversation
          </button>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Demandez a VEGA..."
            rows={1}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none max-h-24"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || sending}
            className="p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          >
            <Send size={16} />
          </button>
        </div>
        <p className="text-[10px] text-gray-400 mt-1.5 text-center">
          VEGA est un outil d'assistance IA. Ses reponses sont indicatives.
        </p>
      </div>
    </>
  );
};

// ─── Vega Panel (slide-in right) ─────────────────────────────────────────────

export const VegaPanel = ({ isOpen, onClose, onNavigate, isAdmin }) => {
  const [tab, setTab] = useState('insights');

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Reset tab on open
  useEffect(() => {
    if (isOpen) setTab('insights');
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md bg-white shadow-2xl flex flex-col h-full animate-slide-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-emerald-600 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <Radar size={20} />
            <div>
              <h2 className="text-base font-bold leading-tight">VEGA</h2>
              <p className="text-emerald-200 text-[10px]">Agent proactif intelligent</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-emerald-700 rounded transition">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 flex-shrink-0">
          <button
            onClick={() => setTab('insights')}
            className={`flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 transition ${
              tab === 'insights'
                ? 'text-emerald-700 border-b-2 border-emerald-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Bell size={14} /> Alertes
          </button>
          <button
            onClick={() => setTab('chat')}
            className={`flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 transition ${
              tab === 'chat'
                ? 'text-emerald-700 border-b-2 border-emerald-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <MessageSquare size={14} /> Chat
          </button>
        </div>

        {/* Tab content */}
        {tab === 'insights' ? (
          <InsightsTab isOpen={isOpen} onNavigate={onNavigate} onClose={onClose} isAdmin={isAdmin} />
        ) : (
          <ChatTab isOpen={isOpen && tab === 'chat'} />
        )}
      </div>
    </div>
  );
};

// ─── Hook for polling summary ────────────────────────────────────────────────

export const useVegaSummary = (enabled = true) => {
  const [summary, setSummary] = useState({ critical: 0, warning: 0, info: 0, total: 0 });

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await getVegaInsightsSummary();
      setSummary(res.data || { critical: 0, warning: 0, info: 0, total: 0 });
    } catch {
      // Silencieux
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const interval = setInterval(refresh, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [refresh, enabled]);

  return { summary, refresh };
};
